import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ReferenceList } from "@/entities/all";

export default function EditReferenceListModal({ isOpen, onClose, onListUpdated, list }) {
  const [formData, setFormData] = useState({
    list_name: '',
    description: '',
    category: 'Technical',
    status: 'Active',
    fuzzy_match_enabled: false,
    fuzzy_match_threshold: 80,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (list) {
      setFormData({
        list_name: list.list_name || '',
        description: list.description || '',
        category: list.category || 'Technical',
        status: list.status || 'Active',
        fuzzy_match_enabled: list.fuzzy_match_enabled || false,
        fuzzy_match_threshold: list.fuzzy_match_threshold ?? 80,
      });
    }
  }, [list, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!list) return;
    setIsSubmitting(true);
    try {
      await ReferenceList.update(list.id, formData);
      onListUpdated();
    } catch (error) {
      console.error('Error updating reference list:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Reference List</DialogTitle>
          <DialogDescription>
            Update the details for the reference list '{list?.list_name}'.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div>
            <Label htmlFor="edit_list_name">List Name</Label>
            <Input
              id="edit_list_name"
              value={formData.list_name}
              onChange={(e) => handleChange('list_name', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="edit_description">Description</Label>
            <Textarea
              id="edit_description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => handleChange('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Geographic">Geographic</SelectItem>
                  <SelectItem value="Corporate">Corporate</SelectItem>
                  <SelectItem value="Regulatory">Regulatory</SelectItem>
                  <SelectItem value="Technical">Technical</SelectItem>
                  <SelectItem value="Financial">Financial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="edit_fuzzy_match_enabled">Fuzzy Matching</Label>
                <p className="text-sm text-muted-foreground">
                  Allow approximate matching for misspelled values
                </p>
              </div>
              <Switch
                id="edit_fuzzy_match_enabled"
                checked={formData.fuzzy_match_enabled}
                onCheckedChange={(checked) => handleChange('fuzzy_match_enabled', checked)}
              />
            </div>

            {formData.fuzzy_match_enabled && (
              <div>
                <Label htmlFor="edit_fuzzy_match_threshold">Match Threshold (0-100)</Label>
                <Input
                  id="edit_fuzzy_match_threshold"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.fuzzy_match_threshold}
                  onChange={(e) => handleChange('fuzzy_match_threshold', parseInt(e.target.value) || 0)}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Higher values require closer matches. Default: 80
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update List'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
